/**
 * A disconnect holds the seat, and the table pauses once it is that seat's
 * turn (play goes on around them until then). There are no turn clocks, so the
 * only way past an absent player is a vote, and the vote is also what handles
 * a seat that has simply gone quiet for three minutes.
 */
import { IDLE_REPLACE_MS, type Seat } from '@cards/shared'
import { Sheet } from './PromptSheet'
import type { TableView } from './view'

export function PauseOverlay({
  view,
  onPropose,
  onVote,
  onReclaim,
}: {
  view: TableView
  onPropose: (seat: Seat) => void
  onVote: (seat: Seat, approve: boolean) => void
  onReclaim: () => void
}): JSX.Element | null {
  const room = view.snapshot.room
  const vote = room.replacementVotes[0] ?? null
  const pause = room.pause

  if (vote) {
    const mine = view.snapshot.you.userId
    const voted = vote.approvals.includes(mine)
    return (
      <Sheet>
        <div className="sheet-body">
          <h2>Swap in a bot for {view.name(vote.seat)}?</h2>
          <p className="muted">
            {vote.approvals.length} of {humanCount(view) - 1} agreed. {view.name(vote.seat)} keeps
            their seat back the moment they return.
          </p>
          <div className="option-row">
            <button className="btn btn-secondary" onClick={() => onVote(vote.seat, false)} disabled={voted}>
              Keep waiting
            </button>
            <button className="btn btn-primary" onClick={() => onVote(vote.seat, true)} disabled={voted}>
              {voted ? 'You agreed' : 'Swap in a bot'}
            </button>
          </div>
        </div>
      </Sheet>
    )
  }

  if (!pause || pause.forSeats.length === 0) return null

  const mySeatPaused = view.mySeat !== null && pause.forSeats.includes(view.mySeat)
  const names = pause.forSeats.map((s) => view.name(s)).join(' and ')
  const waited = Date.now() - pause.since
  const canPropose = !mySeatPaused && waited > IDLE_REPLACE_MS / 6

  return (
    <Sheet>
      <div className="sheet-body">
        <h2>{mySeatPaused ? 'You dropped out' : `Waiting for ${names}`}</h2>
        <p className="muted">
          {mySeatPaused
            ? 'Your seat is being held. Take it back whenever you are ready.'
            : 'The table is paused and the seat is held. Nobody is on a clock.'}
        </p>
        <div className="option-row">
          {mySeatPaused ? (
            <button className="btn btn-primary" onClick={onReclaim}>
              Take my seat back
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              disabled={!canPropose}
              onClick={() => onPropose(pause.forSeats[0])}
            >
              Vote to swap in a bot
            </button>
          )}
        </div>
      </div>
    </Sheet>
  )
}

function humanCount(view: TableView): number {
  return view.snapshot.room.seats.filter((s) => s.kind === 'human').length
}
